export type Locale = "en" | "fr" | "es" | "de" | "pt";

export const localeLabels: Record<Locale, string> = {
  en: "EN",
  fr: "FR",
  es: "ES",
  de: "DE",
  pt: "PT",
};

export type Translations = typeof en;

const en = {
  nav: {
    home: "HOME",
    context: "CONTEXT",
    gaze: "GAZE",
    experience: "EXPERIENCE",
    identity: "IDENTITY",
    when: "WHEN",
    ethics: "ETHICS",
    contact: "CONTACT",
  },
  header: {
    business: "woozoo business",
  },
  hero: {
    tagline: "Know who is really who",
    subtitle:
      "WooZoo reveals and structures a person's public digital identity into a clear, objective and coherent whole.",
  },
  context: {
    badge: "The Context",
    title: "We have access to everything.",
    body: "About companies, restaurants, weather, routes, History, science and the arts.",
  },
  gaze: {
    badge: "A clear gaze",
    title: "No opinion. No interpretation.",
    bankable: "Bankable",
    bankableSub: "SURFACE FINANCIÈRE",
    fame: "Fame",
    fameSub: "Médiatisation",
    lines: [
      "WooZoo relies exclusively on publicly available information and verifiable sources.",
      "No self-declared claims.",
      "No rumors.",
      "No fabricated content.",
      "After signing in, you may submit authoritative public sources to complete your profile.",
      "If validated, they enrich and contextualize your digital identity.",
      "WooZoo creates nothing.",
      "It reveals and structures what already exists.",
    ],
  },
  experience: {
    badge: "The experience",
    title: "Two perspectives. One gaze.",
    forYou: "For You",
    theirProfile: "Their profile",
    steps: [
      "Start with yourself.",
      "Enter your name.",
      "Claim your profile.",
      "WooZoo reveals how your public digital identity manifests itself.",
      "After signing in, you may submit authoritative public sources to complete and refine your profile.",
      "If validated, they help clarify the way you are seen.",
      "You do not change reality.",
      "You ensure it is complete.",
      "Share your WooZoo card — as a structured digital imprint.",
      "As your projects evolve and your achievements grow, you can continue enriching your profile so it remains aligned with who you have become.",
    ],
  },
  identity: {
    badge: "Master Your Digital Identity",
    title: "Your digital identity precedes you.\nIt circulates. It informs. It influences.",
    lines: [
      "WooZoo reveals it as it manifests:\nClear, structured, objective.",
      "You may refine its context through authoritative public sources, verified before integration.",
      "You do not transform it.\nYou illuminate it.",
      "Explore my profile",
    ],
  },
  when: {
    badge: "WHEN USE WOOZOO?",
    title: "Before you meet. Before you decide.",
    beacon: "A social beacon.",
    befores: [
      "Before a personal encounter.",
      "Before a business meeting.",
      "Before a partnership.",
      "Before a hire.",
      "Before an investment.",
      "Before a dinner or worldly event.",
    ],
    moving: "Moving forward without context\nis navigating without reference.",
    lighthouse:
      "WooZoo illuminates signals already visible —\nlike a lighthouse in the night.",
    better:
      "Better context guides better decisions\nand brings depth to human connections.",
  },
  transparency: {
    badge: "TRANSPARENCY & RESPONSIBILITY",
    title: "WooZoo creates nothing.\nIt observes, structures and orders.",
    lines: [
      "It aggregates exclusively publicly available information,\nrendering visible what is already visible,\nin full respect of applicable regulatory frameworks.",
      "A clear gaze.",
      "Nothing more. Nothing less.",
    ],
  },
  contact: {
    title: "Contact us",
    name: "Full name*",
    email: "Email*",
    society: "Company*",
    message: "Your message",
    legals: "Legals",
    help: "Help",
    instagram: "Instagram",
  },
};

const fr: Translations = {
  nav: {
    home: "ACCUEIL",
    context: "CONTEXTE",
    gaze: "REGARD",
    experience: "EXPÉRIENCE",
    identity: "IDENTITÉ",
    when: "QUAND",
    ethics: "ÉTHIQUE",
    contact: "CONTACT",
  },
  header: {
    business: "woozoo business",
  },
  hero: {
    tagline: "Savoir qui est vraiment qui",
    subtitle:
      "WooZoo révèle et structure l'identité numérique publique d'une personne en un ensemble clair, objectif et cohérent.",
  },
  context: {
    badge: "Le Contexte",
    title: "Nous avons accès à tout.",
    body: "Entreprises, restaurants, météo, itinéraires, Histoire, sciences et arts.",
  },
  gaze: {
    badge: "Un regard clair",
    title: "Pas d'opinion. Pas d'interprétation.",
    bankable: "Bankable",
    bankableSub: "SURFACE FINANCIÈRE",
    fame: "Fame",
    fameSub: "Médiatisation",
    lines: [
      "WooZoo s'appuie exclusivement sur des informations publiquement accessibles et des sources vérifiables.",
      "Aucune déclaration auto-proclamée.",
      "Aucune rumeur.",
      "Aucun contenu fabriqué.",
      "Après connexion, vous pouvez soumettre des sources publiques faisant autorité pour compléter votre profil.",
      "Si validées, elles enrichissent et contextualisent votre identité numérique.",
      "WooZoo ne crée rien.",
      "Il révèle et structure ce qui existe déjà.",
    ],
  },
  experience: {
    badge: "L'expérience",
    title: "Deux perspectives. Un regard.",
    forYou: "Pour Vous",
    theirProfile: "Leur profil",
    steps: [
      "Commencez par vous-même.",
      "Entrez votre nom.",
      "Revendiquez votre profil.",
      "WooZoo révèle comment votre identité numérique publique se manifeste.",
      "Après connexion, vous pouvez soumettre des sources publiques faisant autorité pour compléter et affiner votre profil.",
      "Si validées, elles aident à clarifier la façon dont vous êtes perçu.",
      "Vous ne changez pas la réalité.",
      "Vous vous assurez qu'elle est complète.",
      "Partagez votre carte WooZoo — comme une empreinte numérique structurée.",
      "À mesure que vos projets évoluent et que vos réalisations grandissent, vous pouvez continuer à enrichir votre profil pour qu'il reste aligné avec qui vous êtes devenu.",
    ],
  },
  identity: {
    badge: "Maîtrisez Votre Identité Numérique",
    title: "Votre identité numérique vous précède.\nElle circule. Elle informe. Elle influence.",
    lines: [
      "WooZoo la révèle telle qu'elle se manifeste :\nClaire, structurée, objective.",
      "Vous pouvez affiner son contexte grâce à des sources publiques faisant autorité, vérifiées avant intégration.",
      "Vous ne la transformez pas.\nVous l'éclairez.",
      "Explorer mon profil",
    ],
  },
  when: {
    badge: "QUAND UTILISER WOOZOO ?",
    title: "Avant de rencontrer. Avant de décider.",
    beacon: "Un phare social.",
    befores: [
      "Avant une rencontre personnelle.",
      "Avant une réunion professionnelle.",
      "Avant un partenariat.",
      "Avant une embauche.",
      "Avant un investissement.",
      "Avant un dîner ou un événement mondain.",
    ],
    moving: "Avancer sans contexte\nc'est naviguer sans repère.",
    lighthouse:
      "WooZoo éclaire des signaux déjà visibles —\ncomme un phare dans la nuit.",
    better:
      "Un meilleur contexte guide de meilleures décisions\net apporte de la profondeur aux relations humaines.",
  },
  transparency: {
    badge: "TRANSPARENCE & RESPONSABILITÉ",
    title: "WooZoo ne crée rien.\nIl observe, structure et ordonne.",
    lines: [
      "Il agrège exclusivement des informations publiquement accessibles,\nrendant visible ce qui est déjà visible,\ndans le plein respect des cadres réglementaires applicables.",
      "Un regard clair.",
      "Rien de plus. Rien de moins.",
    ],
  },
  contact: {
    title: "Contactez-nous",
    name: "Nom prénom*",
    email: "Email*",
    society: "Société*",
    message: "Votre message",
    legals: "Mentions légales",
    help: "Aide",
    instagram: "Instagram",
  },
};

const es: Translations = {
  nav: {
    home: "INICIO",
    context: "CONTEXTO",
    gaze: "MIRADA",
    experience: "EXPERIENCIA",
    identity: "IDENTIDAD",
    when: "CUÁNDO",
    ethics: "ÉTICA",
    contact: "CONTACTO",
  },
  header: {
    business: "woozoo business",
  },
  hero: {
    tagline: "Saber quién es realmente quién",
    subtitle:
      "WooZoo revela y estructura la identidad digital pública de una persona en un conjunto claro, objetivo y coherente.",
  },
  context: {
    badge: "El Contexto",
    title: "Tenemos acceso a todo.",
    body: "Empresas, restaurantes, clima, rutas, Historia, ciencia y artes.",
  },
  gaze: {
    badge: "Una mirada clara",
    title: "Sin opinión. Sin interpretación.",
    bankable: "Bankable",
    bankableSub: "SUPERFICIE FINANCIERA",
    fame: "Fame",
    fameSub: "Mediatización",
    lines: [
      "WooZoo se basa exclusivamente en información públicamente disponible y fuentes verificables.",
      "Sin declaraciones autoproclamadas.",
      "Sin rumores.",
      "Sin contenido fabricado.",
      "Tras iniciar sesión, puede enviar fuentes públicas autorizadas para completar su perfil.",
      "Si se validan, enriquecen y contextualizan su identidad digital.",
      "WooZoo no crea nada.",
      "Revela y estructura lo que ya existe.",
    ],
  },
  experience: {
    badge: "La experiencia",
    title: "Dos perspectivas. Una mirada.",
    forYou: "Para Ti",
    theirProfile: "Su perfil",
    steps: [
      "Empieza contigo mismo.",
      "Introduce tu nombre.",
      "Reclama tu perfil.",
      "WooZoo revela cómo se manifiesta tu identidad digital pública.",
      "Tras iniciar sesión, puedes enviar fuentes públicas autorizadas para completar y refinar tu perfil.",
      "Si se validan, ayudan a clarificar cómo te perciben.",
      "No cambias la realidad.",
      "Te aseguras de que esté completa.",
      "Comparte tu tarjeta WooZoo — como una huella digital estructurada.",
      "A medida que tus proyectos evolucionan y tus logros crecen, puedes seguir enriqueciendo tu perfil para que siga alineado con quien te has convertido.",
    ],
  },
  identity: {
    badge: "Domina Tu Identidad Digital",
    title: "Tu identidad digital te precede.\nCircula. Informa. Influye.",
    lines: [
      "WooZoo la revela tal como se manifiesta:\nClara, estructurada, objetiva.",
      "Puedes refinar su contexto mediante fuentes públicas autorizadas, verificadas antes de su integración.",
      "No la transformas.\nLa iluminas.",
      "Explorar mi perfil",
    ],
  },
  when: {
    badge: "¿CUÁNDO USAR WOOZOO?",
    title: "Antes de conocer. Antes de decidir.",
    beacon: "Un faro social.",
    befores: [
      "Antes de un encuentro personal.",
      "Antes de una reunión de negocios.",
      "Antes de una asociación.",
      "Antes de una contratación.",
      "Antes de una inversión.",
      "Antes de una cena o evento social.",
    ],
    moving: "Avanzar sin contexto\nes navegar sin referencia.",
    lighthouse:
      "WooZoo ilumina señales ya visibles —\ncomo un faro en la noche.",
    better:
      "Un mejor contexto guía mejores decisiones\ny aporta profundidad a las conexiones humanas.",
  },
  transparency: {
    badge: "TRANSPARENCIA Y RESPONSABILIDAD",
    title: "WooZoo no crea nada.\nObserva, estructura y ordena.",
    lines: [
      "Agrega exclusivamente información públicamente disponible,\nhaciendo visible lo que ya es visible,\nen pleno respeto de los marcos regulatorios aplicables.",
      "Una mirada clara.",
      "Nada más. Nada menos.",
    ],
  },
  contact: {
    title: "Contáctanos",
    name: "Nombre completo*",
    email: "Email*",
    society: "Empresa*",
    message: "Tu mensaje",
    legals: "Legal",
    help: "Ayuda",
    instagram: "Instagram",
  },
};

const de: Translations = {
  nav: {
    home: "START",
    context: "KONTEXT",
    gaze: "BLICK",
    experience: "ERFAHRUNG",
    identity: "IDENTITÄT",
    when: "WANN",
    ethics: "ETHIK",
    contact: "KONTAKT",
  },
  header: {
    business: "woozoo business",
  },
  hero: {
    tagline: "Wissen, wer wirklich wer ist",
    subtitle:
      "WooZoo enthüllt und strukturiert die öffentliche digitale Identität einer Person zu einem klaren, objektiven und kohärenten Ganzen.",
  },
  context: {
    badge: "Der Kontext",
    title: "Wir haben Zugang zu allem.",
    body: "Unternehmen, Restaurants, Wetter, Routen, Geschichte, Wissenschaft und Kunst.",
  },
  gaze: {
    badge: "Ein klarer Blick",
    title: "Keine Meinung. Keine Interpretation.",
    bankable: "Bankable",
    bankableSub: "FINANZIELLE OBERFLÄCHE",
    fame: "Fame",
    fameSub: "Medienpräsenz",
    lines: [
      "WooZoo stützt sich ausschließlich auf öffentlich zugängliche Informationen und überprüfbare Quellen.",
      "Keine Selbsterklärungen.",
      "Keine Gerüchte.",
      "Keine fabrizierten Inhalte.",
      "Nach der Anmeldung können Sie autoritative öffentliche Quellen einreichen, um Ihr Profil zu vervollständigen.",
      "Wenn validiert, bereichern und kontextualisieren sie Ihre digitale Identität.",
      "WooZoo erschafft nichts.",
      "Es enthüllt und strukturiert, was bereits existiert.",
    ],
  },
  experience: {
    badge: "Das Erlebnis",
    title: "Zwei Perspektiven. Ein Blick.",
    forYou: "Für Dich",
    theirProfile: "Ihr Profil",
    steps: [
      "Beginne bei dir selbst.",
      "Gib deinen Namen ein.",
      "Beanspruche dein Profil.",
      "WooZoo enthüllt, wie sich deine öffentliche digitale Identität manifestiert.",
      "Nach der Anmeldung kannst du autoritative öffentliche Quellen einreichen, um dein Profil zu vervollständigen und zu verfeinern.",
      "Wenn validiert, helfen sie zu klären, wie du wahrgenommen wirst.",
      "Du veränderst nicht die Realität.",
      "Du stellst sicher, dass sie vollständig ist.",
      "Teile deine WooZoo-Karte — als strukturierten digitalen Abdruck.",
      "Während sich deine Projekte weiterentwickeln und deine Erfolge wachsen, kannst du dein Profil weiter bereichern, damit es im Einklang mit dem bleibt, wer du geworden bist.",
    ],
  },
  identity: {
    badge: "Beherrsche Deine Digitale Identität",
    title:
      "Deine digitale Identität geht dir voraus.\nSie zirkuliert. Sie informiert. Sie beeinflusst.",
    lines: [
      "WooZoo enthüllt sie, wie sie sich manifestiert:\nKlar, strukturiert, objektiv.",
      "Du kannst ihren Kontext durch autoritative öffentliche Quellen verfeinern, die vor der Integration verifiziert werden.",
      "Du transformierst sie nicht.\nDu erhellst sie.",
      "Mein Profil erkunden",
    ],
  },
  when: {
    badge: "WANN WOOZOO NUTZEN?",
    title: "Bevor du triffst. Bevor du entscheidest.",
    beacon: "Ein sozialer Leuchtturm.",
    befores: [
      "Vor einer persönlichen Begegnung.",
      "Vor einem Geschäftstreffen.",
      "Vor einer Partnerschaft.",
      "Vor einer Einstellung.",
      "Vor einer Investition.",
      "Vor einem Abendessen oder gesellschaftlichen Event.",
    ],
    moving:
      "Ohne Kontext voranzugehen\nist navigieren ohne Orientierung.",
    lighthouse:
      "WooZoo beleuchtet bereits sichtbare Signale —\nwie ein Leuchtturm in der Nacht.",
    better:
      "Besserer Kontext leitet bessere Entscheidungen\nund bringt Tiefe in menschliche Verbindungen.",
  },
  transparency: {
    badge: "TRANSPARENZ & VERANTWORTUNG",
    title: "WooZoo erschafft nichts.\nEs beobachtet, strukturiert und ordnet.",
    lines: [
      "Es aggregiert ausschließlich öffentlich zugängliche Informationen,\nmacht sichtbar, was bereits sichtbar ist,\nin vollem Respekt der geltenden regulatorischen Rahmenbedingungen.",
      "Ein klarer Blick.",
      "Nicht mehr. Nicht weniger.",
    ],
  },
  contact: {
    title: "Kontaktiere uns",
    name: "Vollständiger Name*",
    email: "E-Mail*",
    society: "Unternehmen*",
    message: "Deine Nachricht",
    legals: "Impressum",
    help: "Hilfe",
    instagram: "Instagram",
  },
};

const pt: Translations = {
  nav: {
    home: "INÍCIO",
    context: "CONTEXTO",
    gaze: "OLHAR",
    experience: "EXPERIÊNCIA",
    identity: "IDENTIDADE",
    when: "QUANDO",
    ethics: "ÉTICA",
    contact: "CONTACTO",
  },
  header: {
    business: "woozoo business",
  },
  hero: {
    tagline: "Saber quem é realmente quem",
    subtitle:
      "O WooZoo revela e estrutura a identidade digital pública de uma pessoa num conjunto claro, objetivo e coerente.",
  },
  context: {
    badge: "O Contexto",
    title: "Temos acesso a tudo.",
    body: "Empresas, restaurantes, clima, rotas, História, ciência e artes.",
  },
  gaze: {
    badge: "Um olhar claro",
    title: "Sem opinião. Sem interpretação.",
    bankable: "Bankable",
    bankableSub: "SUPERFÍCIE FINANCEIRA",
    fame: "Fame",
    fameSub: "Mediatização",
    lines: [
      "O WooZoo baseia-se exclusivamente em informações publicamente disponíveis e fontes verificáveis.",
      "Sem declarações autoproclamadas.",
      "Sem rumores.",
      "Sem conteúdo fabricado.",
      "Após iniciar sessão, pode submeter fontes públicas autorizadas para completar o seu perfil.",
      "Se validadas, enriquecem e contextualizam a sua identidade digital.",
      "O WooZoo não cria nada.",
      "Revela e estrutura o que já existe.",
    ],
  },
  experience: {
    badge: "A experiência",
    title: "Duas perspetivas. Um olhar.",
    forYou: "Para Si",
    theirProfile: "O perfil deles",
    steps: [
      "Comece por si mesmo.",
      "Introduza o seu nome.",
      "Reivindique o seu perfil.",
      "O WooZoo revela como a sua identidade digital pública se manifesta.",
      "Após iniciar sessão, pode submeter fontes públicas autorizadas para completar e refinar o seu perfil.",
      "Se validadas, ajudam a clarificar a forma como é percecionado.",
      "Não muda a realidade.",
      "Garante que está completa.",
      "Partilhe o seu cartão WooZoo — como uma impressão digital estruturada.",
      "À medida que os seus projetos evoluem e as suas realizações crescem, pode continuar a enriquecer o seu perfil para que permaneça alinhado com quem se tornou.",
    ],
  },
  identity: {
    badge: "Domine a Sua Identidade Digital",
    title:
      "A sua identidade digital precede-o.\nCircula. Informa. Influencia.",
    lines: [
      "O WooZoo revela-a tal como se manifesta:\nClara, estruturada, objetiva.",
      "Pode refinar o seu contexto através de fontes públicas autorizadas, verificadas antes da integração.",
      "Não a transforma.\nIlumina-a.",
      "Explorar o meu perfil",
    ],
  },
  when: {
    badge: "QUANDO USAR O WOOZOO?",
    title: "Antes de conhecer. Antes de decidir.",
    beacon: "Um farol social.",
    befores: [
      "Antes de um encontro pessoal.",
      "Antes de uma reunião de negócios.",
      "Antes de uma parceria.",
      "Antes de uma contratação.",
      "Antes de um investimento.",
      "Antes de um jantar ou evento social.",
    ],
    moving:
      "Avançar sem contexto\né navegar sem referência.",
    lighthouse:
      "O WooZoo ilumina sinais já visíveis —\ncomo um farol na noite.",
    better:
      "Um melhor contexto guia melhores decisões\ne traz profundidade às conexões humanas.",
  },
  transparency: {
    badge: "TRANSPARÊNCIA E RESPONSABILIDADE",
    title: "O WooZoo não cria nada.\nObserva, estrutura e ordena.",
    lines: [
      "Agrega exclusivamente informações publicamente disponíveis,\ntornando visível o que já é visível,\nem pleno respeito dos quadros regulamentares aplicáveis.",
      "Um olhar claro.",
      "Nada mais. Nada menos.",
    ],
  },
  contact: {
    title: "Contacte-nos",
    name: "Nome completo*",
    email: "Email*",
    society: "Empresa*",
    message: "A sua mensagem",
    legals: "Legal",
    help: "Ajuda",
    instagram: "Instagram",
  },
};

export const translations: Record<Locale, Translations> = { en, fr, es, de, pt };
